import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  ShieldAlert,
  Loader2,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
}

interface Risk {
  id: string;
  title: string;
  description: string;
  probability: number;
  impact: number;
  category: string;
  mitigation_plan: string;
  owner: string;
  status: "identified" | "mitigating" | "accepted" | "closed";
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  identified: "bg-blue-100 text-blue-800 border-blue-300",
  mitigating: "bg-amber-100 text-amber-800 border-amber-300",
  accepted: "bg-gray-100 text-gray-600 border-gray-300",
  closed: "bg-green-100 text-green-800 border-green-300",
};

function riskScore(p: number, i: number) {
  return p * i;
}

function scoreColor(score: number): string {
  if (score <= 6) return "bg-green-500";
  if (score <= 12) return "bg-amber-500";
  return "bg-red-500";
}

function scoreBadgeColor(score: number): string {
  if (score <= 6) return "bg-green-100 text-green-800 border-green-300";
  if (score <= 12) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-red-100 text-red-800 border-red-300";
}

function cellColor(p: number, i: number): string {
  const s = riskScore(p, i);
  if (s <= 6) return "bg-green-100 hover:bg-green-200";
  if (s <= 12) return "bg-amber-100 hover:bg-amber-200";
  return "bg-red-100 hover:bg-red-200";
}

const EMPTY_FORM = {
  title: "",
  description: "",
  probability: 1,
  impact: 1,
  category: "",
  mitigation_plan: "",
  owner: "",
  status: "identified" as Risk["status"],
};

export default function RiskRegisterPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [risks, setRisks] = useState<Risk[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (baProjectId) load();
    else setRisks([]);
  }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/api/tools/risks?project_id=${baProjectId}`,
        { headers: authHeader }
      );
      setRisks(res.data || []);
    } catch (e) {
      console.error("Failed to load risks", e);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (r: Risk) => {
    setEditingId(r.id);
    setForm({
      title: r.title,
      description: r.description,
      probability: r.probability,
      impact: r.impact,
      category: r.category,
      mitigation_plan: r.mitigation_plan,
      owner: r.owner,
      status: r.status,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${BASE}/api/tools/risks/${editingId}`, form, {
          headers: authHeader,
        });
      } else {
        await axios.post(
          `${BASE}/api/tools/risks`,
          { ...form, project_id: baProjectId },
          { headers: authHeader }
        );
      }
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
      load();
    } catch (e) {
      console.error("Failed to save risk", e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await axios.delete(`${BASE}/api/tools/risks/${id}`, {
        headers: authHeader,
      });
      setDeleteConfirmId(null);
      load();
    } catch (e) {
      console.error("Failed to delete risk", e);
    }
  };

  // Build heatmap counts: grid[p][i] = count of risks at that p/i
  const heatmapCounts: Record<string, number> = {};
  risks.forEach((r) => {
    const key = `${r.probability}-${r.impact}`;
    heatmapCounts[key] = (heatmapCounts[key] || 0) + 1;
  });

  const sortedRisks = [...risks].sort(
    (a, b) => riskScore(b.probability, b.impact) - riskScore(a.probability, a.impact)
  );

  if (!baProjectId) {
    return (
      <div className="h-full flex items-center justify-center text-[#5C5C57]">
        <p className="text-sm">No project selected.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-[#314E6E]" />
          <h2 className="text-lg font-semibold text-[#1E1E1C]">Risk Register</h2>
          <span className="text-xs text-[#5C5C57] bg-[#F7F4EE] border border-[#D9D4CC] rounded-full px-2 py-0.5">
            {risks.length}
          </span>
        </div>
        <Button
          size="sm"
          onClick={openNew}
          className="bg-[#314E6E] hover:bg-[#243b54] text-white gap-1"
        >
          <Plus className="h-4 w-4" />
          Add Risk
        </Button>
      </div>

      {/* Heatmap */}
      {risks.length > 0 && (
        <div className="bg-[#FCFBF8] border border-[#D9D4CC] rounded-xl p-4 mb-5">
          <p className="text-xs font-semibold text-[#5C5C57] uppercase tracking-wide mb-3">
            Risk Heatmap
          </p>
          <div className="flex gap-3">
            {/* Y-axis label */}
            <div className="flex flex-col justify-between items-center pr-1 py-1">
              {[5, 4, 3, 2, 1].map((p) => (
                <span key={p} className="text-[10px] text-[#5C5C57] w-3 text-center leading-none">
                  {p}
                </span>
              ))}
              <span className="text-[9px] text-[#5C5C57] mt-1 writing-mode-vertical opacity-60">
                P
              </span>
            </div>
            {/* Grid */}
            <div className="flex-1">
              <div className="grid gap-1" style={{ gridTemplateRows: "repeat(5, 1fr)" }}>
                {[5, 4, 3, 2, 1].map((p) => (
                  <div key={p} className="grid gap-1" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
                    {[1, 2, 3, 4, 5].map((i) => {
                      const count = heatmapCounts[`${p}-${i}`] || 0;
                      return (
                        <div
                          key={i}
                          className={`${cellColor(p, i)} rounded flex items-center justify-center text-xs font-semibold text-gray-700 transition-colors`}
                          style={{ aspectRatio: "1", minHeight: "28px" }}
                        >
                          {count > 0 ? count : ""}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              {/* X-axis */}
              <div className="grid gap-1 mt-1" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <span key={i} className="text-[10px] text-[#5C5C57] text-center">
                    {i}
                  </span>
                ))}
              </div>
              <p className="text-[9px] text-[#5C5C57] text-center mt-0.5 opacity-60">
                Impact
              </p>
            </div>
          </div>
          {/* Legend */}
          <div className="flex gap-4 mt-3">
            {[
              { label: "Low (≤6)", color: "bg-green-200" },
              { label: "Medium (7-12)", color: "bg-amber-200" },
              { label: "High (13-25)", color: "bg-red-200" },
            ].map(({ label, color }) => (
              <div key={label} className="flex items-center gap-1.5">
                <div className={`w-3 h-3 rounded ${color}`} />
                <span className="text-[10px] text-[#5C5C57]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[#314E6E]" />
        </div>
      )}

      {/* Empty state */}
      {!loading && risks.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-[#5C5C57]">
          <ShieldAlert className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm font-medium">No risks identified yet.</p>
          <p className="text-xs mt-1 opacity-70">
            Track risks, probabilities, and mitigation plans.
          </p>
        </div>
      )}

      {/* Risk list */}
      {!loading && sortedRisks.length > 0 && (
        <div className="space-y-3">
          {sortedRisks.map((r) => {
            const score = riskScore(r.probability, r.impact);
            const expanded = expandedId === r.id;
            return (
              <div
                key={r.id}
                className="bg-[#FCFBF8] border border-[#D9D4CC] rounded-xl p-4"
              >
                <div
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() =>
                    setExpandedId(expanded ? null : r.id)
                  }
                >
                  {/* Score indicator */}
                  <div
                    className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm ${scoreColor(score)}`}
                  >
                    {score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-[#1E1E1C] text-sm">
                        {r.title}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-xs capitalize ${STATUS_COLORS[r.status]}`}
                      >
                        {r.status}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`text-xs ${scoreBadgeColor(score)}`}
                      >
                        P{r.probability} × I{r.impact}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#5C5C57] flex-wrap">
                      {r.category && <span className="capitalize">{r.category}</span>}
                      {r.owner && <span>Owner: {r.owner}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(r);
                      }}
                      className="p-1.5 rounded-lg hover:bg-[#F7F4EE] text-[#5C5C57] hover:text-[#1E1E1C] transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(r.id);
                      }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-[#5C5C57] hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {expanded ? (
                      <ChevronUp className="h-4 w-4 text-[#5C5C57]" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-[#5C5C57]" />
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="mt-4 space-y-3 border-t border-[#D9D4CC] pt-4">
                    {r.description && (
                      <div>
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide mb-1">
                          Description
                        </p>
                        <p className="text-sm text-[#5C5C57] whitespace-pre-wrap">
                          {r.description}
                        </p>
                      </div>
                    )}
                    {r.mitigation_plan && (
                      <div>
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide mb-1">
                          Mitigation Plan
                        </p>
                        <p className="text-sm text-[#5C5C57] whitespace-pre-wrap">
                          {r.mitigation_plan}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">
              {editingId ? "Edit Risk" : "Add Risk"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Risk title"
                className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Description
              </label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Describe the risk"
                rows={2}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Probability (1-5)
                </label>
                <Select
                  value={String(form.probability)}
                  onValueChange={(v) =>
                    setForm({ ...form, probability: Number(v) })
                  }
                >
                  <SelectTrigger className="border-[#D9D4CC] bg-white text-[#1E1E1C]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Impact (1-5)
                </label>
                <Select
                  value={String(form.impact)}
                  onValueChange={(v) =>
                    setForm({ ...form, impact: Number(v) })
                  }
                >
                  <SelectTrigger className="border-[#D9D4CC] bg-white text-[#1E1E1C]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-[#F7F4EE] border border-[#D9D4CC] text-sm text-[#1E1E1C]">
              Risk Score:{" "}
              <span
                className={`font-bold ${
                  riskScore(form.probability, form.impact) <= 6
                    ? "text-green-700"
                    : riskScore(form.probability, form.impact) <= 12
                    ? "text-amber-700"
                    : "text-red-700"
                }`}
              >
                {riskScore(form.probability, form.impact)}
              </span>
              <span className="text-[#5C5C57] text-xs ml-2">
                (P{form.probability} × I{form.impact})
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Category
                </label>
                <Input
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  placeholder="e.g. Technical"
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Owner
                </label>
                <Input
                  value={form.owner}
                  onChange={(e) =>
                    setForm({ ...form, owner: e.target.value })
                  }
                  placeholder="Risk owner"
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Mitigation Plan
              </label>
              <Textarea
                value={form.mitigation_plan}
                onChange={(e) =>
                  setForm({ ...form, mitigation_plan: e.target.value })
                }
                placeholder="How will this risk be mitigated?"
                rows={2}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Status
              </label>
              <Select
                value={form.status}
                onValueChange={(v) =>
                  setForm({ ...form, status: v as Risk["status"] })
                }
              >
                <SelectTrigger className="border-[#D9D4CC] bg-white text-[#1E1E1C]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="identified">Identified</SelectItem>
                  <SelectItem value="mitigating">Mitigating</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(false)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !form.title.trim()}
              className="bg-[#314E6E] hover:bg-[#243b54] text-white"
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingId ? "Save Changes" : "Add Risk"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteConfirmId}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <DialogContent className="max-w-sm bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">Delete Risk?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5C57] mt-1">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteConfirmId(null)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => deleteConfirmId && remove(deleteConfirmId)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
